import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { useSelector } from 'react-redux';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronRightIcon, Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';
import type { Language, Block } from '@worldathletics/types';
import type { SortingState } from '@tanstack/react-table';
import { z } from 'zod';

import {
  PageContentLayout,
  PageContentLayoutHeader,
  PageContentLayoutMain,
  PageContentLayoutSubheader,
} from '@/components/layout';
import { Button, Pagination } from '@/components/atoms';
import { CustomTitle } from '@/components/typography';
import {
  ReferencesReviewFilters,
  ReferencesReviewTable,
  buildLabelEntryForRow,
  castReferenceReviewRows,
  computeReferenceReviewCounts,
  filterReferencesByCategory,
  filterReferencesForNode,
  resolveReferenceReviewLinkLabel,
  useReferenceDestinationLabels,
  useRemoveReference,
  type ReferenceReviewFilter,
  type ReferenceReviewRow,
} from '@/components/references-review';
import { type EditLinkSourceRef } from '@/components/modals/edit-link';
import { getImportDiagnosticsMemory } from '@/lib/import-diagnostics-memory';
import { downloadImportDiagnosticsTxt } from '@/lib/import-diagnostics-download';
import { isImportLogsEnvironment } from '@/lib/is-import-logs-environment';
import {
  clearReviewOnMountFlag,
  loadImportDiagnostics,
  markReferencesReviewDismissed,
} from '@/lib/import-diagnostics-storage';
import { useReferenceTargetLabelMaps } from '@/hooks/use-reference-target-label-maps';
import { resolveLiveSourceBlocks } from '@/lib/resolve-live-source-blocks';
import { usePermission } from '@/hooks/usePermission';
import { ReferencesReviewEditModal } from '@/components/references-review/references-review-edit-modal';
import { useDocumentQuery } from '@/query/book/useDocumentQuery';
import { useDocumentNodeQuery } from '@/query/node/useDocumentNodeQuery';
import { useReferencesQuery } from '@/query/references/useReferencesQuery';
import { requirePermission } from '@/lib/route-guards';
import { Permission, canEditDocumentNode } from '@worldathletics/utilities';
import type { RootState } from '@/context/store/store';
import '@/styles/route-references-review.scss';

const referencesReviewSearchSchema = z.object({
  nodeId: z.string(),
  context: z.enum(['post-upload', 'review']).default('review'),
  filter: z
    .enum(['all', 'crossBook', 'lost', 'external'])
    .default('all'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),
  sort: z.enum(['links', 'from']).default('links'),
  order: z.enum(['asc', 'desc']).default('asc'),
});

export const Route = createFileRoute('/_auth/document/$docId/references-review')({
  validateSearch: referencesReviewSearchSchema,
  beforeLoad: () => {
    requirePermission(Permission.DocumentsEdit);
  },
  component: ReferencesReviewRoute,
});

function toEditLinkSourceRef(
  row: ReferenceReviewRow,
  bookId: string,
  language: Language,
): EditLinkSourceRef {
  return {
    id: row.id,
    sourceId: row.sourceId,
    nodeId: row.nodeId,
    bookId,
    refId: row.refId ?? null,
    position: 0,
    targetBookId: row.targetBookId ?? null,
    targetNodeId: row.targetNodeId ?? null,
    customName: row.customName,
    language,
    isLost: !row.resolved && row.status !== 'ignored' && row.kind !== 'url',
  };
}

function ReferencesReviewRoute() {
  const { docId } = Route.useParams();
  const search = Route.useSearch();
  const { nodeId } = search;
  const navigate = useNavigate();
  const hasEditPermission = usePermission(Permission.DocumentsEdit);
  const authUser = useSelector((state: RootState) => state.auth.user);

  const documentBook = useDocumentQuery(docId);
  const documentNode = useDocumentNodeQuery(docId, nodeId);
  const documentDataState = useSelector((state: RootState) => state.documentData);

  const canEditReferences = useMemo(() => {
    if (!hasEditPermission || !documentBook.data || !documentNode.data) {
      return false;
    }
    return canEditDocumentNode({
      nodeState: documentNode.data.state,
      bookState: documentBook.data.state,
      role: authUser?.role ?? 'reader',
    });
  }, [
    authUser?.role,
    documentBook.data,
    documentNode.data,
    hasEditPermission,
  ]);
  const referencesQuery = useReferencesQuery(docId);
  const { removeReference, isRemoving } = useRemoveReference(docId, nodeId);
  const [isNavigating, startNavigation] = useTransition();
  const [removingRowId, setRemovingRowId] = useState<string | null>(null);

  const [editLinkState, setEditLinkState] = useState<{
    ref: EditLinkSourceRef;
    mode: 'create' | 'edit';
    openInUrlMode?: boolean;
    initialUrl?: string;
  } | null>(null);
  const [importDiagnostics, setImportDiagnostics] = useState(
    () => getImportDiagnosticsMemory(docId) ?? loadImportDiagnostics(docId),
  );

  useEffect(() => {
    const parsed =
      getImportDiagnosticsMemory(docId) ?? loadImportDiagnostics(docId);
    if (parsed) {
      requestAnimationFrame(() => {
        setImportDiagnostics(parsed);
      });
    }
  }, [docId]);

  const nodeScopedReferences = useMemo(() => {
    const rows = castReferenceReviewRows(referencesQuery.data);
    return filterReferencesForNode(rows, nodeId);
  }, [referencesQuery.data, nodeId]);

  const serverSourceBlocks = (documentNode.data?.blocks ?? []) as Block[];
  const sourceNodeKey = `${docId}:${nodeId}`;

  const { blocks: liveSourceBlocks, structuralSignature } = useMemo(
    () =>
      resolveLiveSourceBlocks(
        serverSourceBlocks,
        documentDataState,
        sourceNodeKey,
      ),
    [serverSourceBlocks, documentDataState, sourceNodeKey],
  );

  const targetLabelMaps = useReferenceTargetLabelMaps({
    documentId: docId,
    nodeId,
    documentDataState,
    documentNode: documentNode.data
      ? {
        blocks: liveSourceBlocks,
        numberingStyle: documentNode.data.numberingStyle,
        language: documentNode.data.language,
      }
      : undefined,
    references: nodeScopedReferences,
    sourceBlocks: liveSourceBlocks,
    structuralSignature,
  });

  const destinationLabels = useReferenceDestinationLabels({
    bookId: docId,
    nodeId,
    sourceBookName: documentBook.data?.name ?? '',
    sourceNode: documentNode.data
      ? {
        blocks: liveSourceBlocks,
        numberingStyle: documentNode.data.numberingStyle,
        language: documentNode.data.language,
      }
      : undefined,
    references: nodeScopedReferences,
    targetLabelMaps,
  });

  const counts = useMemo(
    () => computeReferenceReviewCounts(nodeScopedReferences, docId),
    [nodeScopedReferences, docId],
  );

  const filteredReferences = useMemo(
    () =>
      filterReferencesByCategory(
        nodeScopedReferences,
        docId,
        search.filter as ReferenceReviewFilter,
      ),
    [nodeScopedReferences, docId, search.filter],
  );

  const tableRows = useMemo(() => {
    const blocks = liveSourceBlocks ?? [];
    const numberingStyle = documentNode.data?.numberingStyle;
    const language = documentNode.data?.language;

    return filteredReferences.map(row => ({
      ...row,
      fromLabel: destinationLabels.get(row.id) ?? row.displayName,
      linkLabel:
        numberingStyle && language
          ? resolveReferenceReviewLinkLabel(
            row,
            docId,
            buildLabelEntryForRow(
              row,
              docId,
              nodeId,
              blocks,
              numberingStyle,
              language,
              targetLabelMaps,
            ),
          )
          : resolveReferenceReviewLinkLabel(row, docId, undefined),
    }));
  }, [
    filteredReferences,
    destinationLabels,
    docId,
    nodeId,
    liveSourceBlocks,
    documentNode.data,
    targetLabelMaps,
  ]);

  const sortingState = useMemo((): SortingState => {
    return [{ id: search.sort, desc: search.order === 'desc' }];
  }, [search.sort, search.order]);

  const sortedRows = useMemo(() => {
    const sorted = [...tableRows];
    const column = search.sort;
    const direction = search.order === 'desc' ? -1 : 1;
    sorted.sort((a, b) => {
      const aVal = column === 'links' ? a.linkLabel : a.fromLabel;
      const bVal = column === 'links' ? b.linkLabel : b.fromLabel;
      return aVal.localeCompare(bVal) * direction;
    });
    return sorted;
  }, [tableRows, search.sort, search.order]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / search.limit));
  const currentPage = Math.min(search.page, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * search.limit;
    return sortedRows.slice(start, start + search.limit);
  }, [sortedRows, currentPage, search.limit]);

  const pageTitle =
    search.context === 'post-upload' ? 'Upload completed' : 'References review';
  const primaryActionLabel = search.context === 'post-upload' ? 'Next' : 'Done';
  const showDownloadLogs =
    isImportLogsEnvironment() && importDiagnostics !== null;

  function handleDownloadLogs() {
    if (!importDiagnostics) return;
    downloadImportDiagnosticsTxt(
      importDiagnostics,
      documentBook.data?.name,
    );
  }

  function handleSortingChange(next: SortingState) {
    if (next.length === 0) {
      startNavigation(() => {
        void navigate({
          to: '/document/$docId/references-review',
          params: { docId },
          search: {
            ...search,
            order: 'asc',
            page: 1,
          },
        });
      });
      return;
    }

    const entry = next[0];
    startNavigation(() => {
      void navigate({
        to: '/document/$docId/references-review',
        params: { docId },
        search: {
          ...search,
          sort: entry.id as 'links' | 'from',
          order: entry.desc ? 'desc' : 'asc',
          page: 1,
        },
      });
    });
  }

  function handlePrimaryAction() {
    markReferencesReviewDismissed(docId);
    clearReviewOnMountFlag(docId);
    startNavigation(() => {
      void navigate({
        to: '/document/$docId/$nodeId',
        params: { docId, nodeId },
        search: {
          language: documentNode.data?.language,
          edit: false,
        },
      });
    });
  }

  const handleRemove = useCallback(
    async (row: ReferenceReviewRow) => {
      if (!documentNode.data) return;
      const blocks = documentNode.data.blocks as Block[];
      setRemovingRowId(row.id);
      try {
        await removeReference({
          row,
          allRows: nodeScopedReferences,
          blocks,
          getLabelEntry: ref =>
            buildLabelEntryForRow(
              ref,
              docId,
              nodeId,
              blocks,
              documentNode.data!.numberingStyle,
              documentNode.data!.language,
              targetLabelMaps,
            ),
        });
        toast.success('Reference removed');
      } finally {
        setRemovingRowId(null);
      }
    },
    [
      documentNode.data,
      removeReference,
      docId,
      nodeId,
      targetLabelMaps,
      nodeScopedReferences,
    ],
  );

  const handleEdit = useCallback(
    (row: ReferenceReviewRow) => {
      if (!documentNode.data) return;
      if (row.kind === 'url' && row.externalUrl) {
        setEditLinkState({
          ref: toEditLinkSourceRef(row, docId, documentNode.data.language),
          mode: 'edit',
          openInUrlMode: true,
          initialUrl: row.externalUrl,
        });
        return;
      }
      setEditLinkState({
        ref: toEditLinkSourceRef(row, docId, documentNode.data.language),
        mode: 'edit',
      });
    },
    [documentNode.data, docId],
  );

  const handleSelectSection = useCallback(
    (row: ReferenceReviewRow) => {
      if (!documentNode.data) return;
      setEditLinkState({
        ref: toEditLinkSourceRef(row, docId, documentNode.data.language),
        mode: 'edit',
      });
    },
    [documentNode.data, docId],
  );

  const handleCloseEditModal = useCallback(() => {
    setEditLinkState(null);
  }, []);

  const handleRemoveRow = useCallback(
    (row: ReferenceReviewRow) => {
      void handleRemove(row);
    },
    [handleRemove],
  );

  const isLoading =
    documentBook.isLoading ||
    documentNode.isLoading ||
    referencesQuery.isLoading;
  const referencesError = referencesQuery.isError;

  return (
    <PageContentLayout>
      <PageContentLayoutHeader
        buttons={
          <Button
            type="button"
            variant="default"
            onClick={handlePrimaryAction}
            disabled={isNavigating}
          >
            {isNavigating ? (
              <Loader2Icon size={16} className="animate-spin" />
            ) : (
              <>
                {primaryActionLabel} <ChevronRightIcon size={16} />
              </>
            )}
          </Button>
        }
      >
        <CustomTitle level="h1">{pageTitle}</CustomTitle>
      </PageContentLayoutHeader>

      <PageContentLayoutSubheader className="references-review-subheader">
        <ReferencesReviewFilters
          counts={counts}
          activeFilter={search.filter as ReferenceReviewFilter}
          docId={docId}
          searchParams={search}
          showDownloadLogs={showDownloadLogs}
          onDownloadLogs={handleDownloadLogs}
          onFilterChange={filter => {
            startNavigation(() => {
              void navigate({
                to: '/document/$docId/references-review',
                params: { docId },
                search: {
                  ...search,
                  filter,
                  page: 1,
                },
              });
            });
          }}
        />
      </PageContentLayoutSubheader>

      <PageContentLayoutMain>
        <ReferencesReviewTable
          rows={paginatedRows}
          isLoading={isLoading}
          isError={referencesError}
          isPending={isNavigating}
          removingRowId={isRemoving ? removingRowId : null}
          canEdit={canEditReferences}
          sorting={sortingState}
          onSortingChange={handleSortingChange}
          onEdit={handleEdit}
          onRemove={handleRemoveRow}
          onSelectSection={handleSelectSection}
        />
      </PageContentLayoutMain>

      {sortedRows.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPageCall={() => {
            startNavigation(() => {
              void navigate({
                to: '/document/$docId/references-review',
                params: { docId },
                search: {
                  ...search,
                  page: Math.max(1, currentPage - 1),
                },
              });
            });
          }}
          onNextPageCall={() => {
            startNavigation(() => {
              void navigate({
                to: '/document/$docId/references-review',
                params: { docId },
                search: {
                  ...search,
                  page: Math.min(totalPages, currentPage + 1),
                },
              });
            });
          }}
        />
      )}

      {editLinkState && (
        <ReferencesReviewEditModal
          docId={docId}
          nodeId={nodeId}
          editLinkState={editLinkState}
          onClose={handleCloseEditModal}
        />
      )}
    </PageContentLayout>
  );
}
